import React, { useState, useCallback, useContext, useEffect, useMemo } from 'react'
import { getUnixTime } from 'date-fns'
import NumberScroller from '../_control/NumberScroller'
import { css } from '@emotion/react'
import Selector from '../_control/OptionsSelector'
import useRawPromise from '../../hooks/useRawPromise'
import FormLoader from './FormLoader'
import useBridge from '../../hooks/useBridge'
import EventDateTimePicker from '../_control/EventDateTimePicker'
import { useIntl } from 'react-intl'
import capitalize from 'voca/capitalize'
import PitchContext from '../../context/Pitch'
import EventFormSubmit from './EventFormSubmit'

export default function LineMarkingForm({
    eventType,
    event,
    onPostRequest
}) {
    //utils
    const { post, put, get } = useRawPromise()
    const { formatMessage } = useIntl()
    //field state
    const [selectedDateTime, setSelectedDateTime] = useState()
    const [timeTaken, setTimeTaken] = useState(0)
    const [lineMarkingMethod, setLineMarkingMethod] = useState()
    const [amount, setAmount] = useState(0)
    //other
    const { data: lineMarkingMethodOptions, loading: waitingForTypes } = useBridge(`/api/current/frontend-bridge/line-marking-methods`)
    const { data: pitch } = useContext(PitchContext)

    const [formError, setFormError] = useState()
    const [isLoading, setIsLoading] = useState(!!event)

    useEffect(() => {
        if (event) {
            async function getEventAndHydrateFormData(eventId) {
                setIsLoading(true)
                const { data } = await get(`/api/current/line-marking-events/${eventId}`)
                setSelectedDateTime(new Date(data.timestamp * 1000))
                setTimeTaken(data.timeTaken / 60)
                setLineMarkingMethod(data.lineMarkingMethod)
                setAmount(data.amount)
                setIsLoading(false)
            }
            getEventAndHydrateFormData(event.id)
        }
    }, [event])

    const hasRequiredFields = useMemo(() => {
        return !!(Number.isFinite(timeTaken) && lineMarkingMethod)
    }, [timeTaken, lineMarkingMethod])

    const validateAndSubmit = useCallback(async () => {
        if (!lineMarkingMethod) return setFormError(formatMessage({ id: 'pleaseSelectMethod' }))

        if (!Number.isFinite(timeTaken) || !Number.isFinite(amount)) return setFormError(formatMessage({ id: 'pleaseInputValidNum' }))

        setIsLoading(true)

        const payload = {
            timestamp: getUnixTime(selectedDateTime),
            timeTaken: parseInt(timeTaken * 60),
            lineMarkingMethod,
            amount,
        }

        if (event) {
            const { data, error } = await put(`${event['@id']}`, { body: payload })
            if (error) {
                setIsLoading(false)
                onPostRequest(false, error)
            }
            else if (data) {
                onPostRequest(true, data)
            }
        } else {
            const newPayload = {
                ...payload,
                name: `LineMarking event for pitch ${pitch['id']}`,
                pitch: pitch['@id'],
            }
            const { data, error } = await post(`/api/current/${eventType.path}`, { body: newPayload })
            if (error) {
                setIsLoading(false)
                onPostRequest(false, error)
            }
            else if (data) {
                onPostRequest(true, data)
            }
        }

    }, [lineMarkingMethod, amount, pitch, selectedDateTime, timeTaken])

    const isBusy = useMemo(() => {
        return waitingForTypes || isLoading
    }, [waitingForTypes, isLoading])

    return (<>
        {isBusy ? <FormLoader /> :
            (<div css={css`display: flex; flex-direction: column; width: 100%; align-items: flex-start;`}>
                <EventDateTimePicker
                    unixDate={selectedDateTime}
                    setUnixDate={setSelectedDateTime}
                />
                <div css={css`display: flex; flex-wrap: wrap; margin: 1em 0; gap: 0 4em;`}>
                    <NumberScroller
                        label={formatMessage({ id: 'timeTaken' })}
                        minVal={0}
                        maxVal={24}
                        state={timeTaken}
                        unit={capitalize(formatMessage({ id: 'hours' }))}
                        step={0.5}
                        setState={setTimeTaken}
                    />
                    <NumberScroller
                        label={formatMessage({ id: 'amount' })}
                        unit={'L'}
                        minVal={0}
                        maxVal={10000}
                        setState={setAmount}
                        state={amount}
                    />
                </div>
                <Selector
                    label={formatMessage({ id: 'method' })}
                    optionsObject={lineMarkingMethodOptions}
                    setState={setLineMarkingMethod}
                    state={lineMarkingMethod}
                    isRequired={true}
                />
            </div>)
        }

        <EventFormSubmit
            onSubmit={validateAndSubmit}
            loading={isBusy}
            disabled={!hasRequiredFields}
            errorMsg={formError}
        />
    </>)
}